import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

import { fetchList } from '@pepper/utils/src/api/assignments/assignments'
import { useShowMessage } from '../providers'

type ReturnType = {
  records: Assignment[]
  totalRecords: number
  currentPage: number
  isLoading: boolean
  isFetching: boolean
  refetch: () => void
  error: Error | null
}

type DataModal = {
  records: Assignment[]
  total_records: number
}

export const usePaginatedAssignmentList = (
  queryParams: Partial<AssignmentQueryParams> = {},
  page_size = 25,
  isPaginatedList = true,
  isV2 = false,
  enabled = true
): ReturnType => {
  const showMessage = useShowMessage()

  const handleApiRequest = async () => {
    const { status, data, error } = await fetchList({
      query_params: queryParams,
      page: queryParams.current_page,
      page_size,
      isPaginatedList,
      isV2
    })

    if (status === 'error') {
      throw new Error(error)
    }

    return data
  }

  const { isLoading, isFetching, isError, error, refetch, data } = useQuery<
    DataModal,
    Error
  >({
    queryKey: ['assignments', queryParams.current_page, page_size, queryParams],
    queryFn: handleApiRequest,
    enabled,
    retry: true,
    keepPreviousData: isPaginatedList
  })

  useEffect(() => {
    if (isError) {
      showMessage({ type: 'error', message: error?.message })
    }
  }, [isError, error, showMessage])

  return {
    records: data?.records ?? [],
    totalRecords: data?.total_records ?? 0,
    currentPage: queryParams.current_page || 0,
    isLoading,
    isFetching,
    refetch,
    error
  }
}
